import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Plus, Pencil, Trash2, Loader2, Users, DollarSign, Building2, FileDown, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function AdminAgents() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("agents");
  
  // Agents state
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    commission_rate: 10,
    is_active: true,
    notes: ""
  });
  
  // Commissions state
  const [commissions, setCommissions] = useState([]);
  const [commissionFilter, setCommissionFilter] = useState("all");
  const [commissionsLoading, setCommissionsLoading] = useState(true);
  
  // Companies for assignment
  const [companies, setCompanies] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigningCompany, setAssigningCompany] = useState(null);

  useEffect(() => {
    if (token) {
      fetchAgents();
      fetchCommissions();
      fetchCompanies();
    }
  }, [token]);

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/admin/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento agenti");
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissions = async () => {
    try {
      const params = commissionFilter !== "all" ? `?status=${commissionFilter}` : "";
      const res = await axios.get(`${API}/admin/commissions${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissions(res.data);
    } catch (error) {
      console.error("Errore commissions", error);
    } finally {
      setCommissionsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data);
    } catch (error) {
      console.error("Errore companies", error);
    }
  };

  useEffect(() => {
    setCommissionsLoading(true);
    fetchCommissions();
  }, [commissionFilter]);

  const handleCreate = () => {
    setEditingAgent(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      commission_rate: 10,
      is_active: true,
      notes: ""
    });
    setShowDialog(true);
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      email: agent.email,
      phone: agent.phone || "",
      commission_rate: agent.commission_rate,
      is_active: agent.is_active,
      notes: agent.notes || ""
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Nome e email sono obbligatori");
      return;
    }

    setSaving(true);
    try {
      if (editingAgent) {
        await axios.put(`${API}/admin/agents/${editingAgent.agent_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Agente aggiornato");
      } else {
        await axios.post(`${API}/admin/agents`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Agente creato");
      }
      setShowDialog(false);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`Eliminare l'agente ${agent.name}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/agents/${agent.agent_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agente eliminato");
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleAssignAgent = async (company, agentId) => {
    try {
      await axios.put(`${API}/admin/companies/${company.company_id}/agent`, 
        { agent_id: agentId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(agentId ? "Agente assegnato" : "Agente rimosso");
      fetchCompanies();
      fetchAgents();
      setShowAssignDialog(false);
    } catch (error) {
      toast.error("Errore nell'assegnazione");
    }
  };

  const handlePayCommission = async (commission) => {
    if (!window.confirm(`Confermare pagamento commissione di €${commission.commission_amount.toFixed(2)}?`)) return;
    
    try {
      await axios.put(`${API}/admin/commissions/${commission.commission_id}/pay`, 
        { status: "paid" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Commissione pagata");
      fetchCommissions();
      fetchAgents();
    } catch (error) {
      toast.error("Errore nel pagamento");
    }
  };

  const handleExportCSV = () => {
    const params = commissionFilter !== "all" ? `?status=${commissionFilter}` : "";
    window.open(`${API}/admin/commissions/export${params}`, "_blank");
  };

  // Calculate totals
  const totalPending = commissions.filter(c => c.status === "pending").reduce((sum, c) => sum + c.commission_amount, 0);
  const totalPaid = commissions.filter(c => c.status === "paid").reduce((sum, c) => sum + c.commission_amount, 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Agenti <span className="text-primary">Commerciali</span>
          </h1>
          <p className="text-sm text-muted-foreground">Gestione rete vendita e commissioni</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-dashboard text-center">
            <p className="text-2xl font-bold font-mono">{agents.length}</p>
            <p className="text-xs text-muted-foreground">Agenti Totali</p>
          </div>
          <div className="card-dashboard text-center">
            <p className="text-2xl font-bold font-mono">{agents.filter(a => a.is_active).length}</p>
            <p className="text-xs text-muted-foreground">Agenti Attivi</p>
          </div>
          <div className="card-dashboard text-center border-yellow-500">
            <p className="text-2xl font-bold font-mono text-yellow-600">€{totalPending.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">Da Pagare</p>
          </div>
          <div className="card-dashboard text-center border-green-500">
            <p className="text-2xl font-bold font-mono text-green-600">€{totalPaid.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">Pagate</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Agenti
            </TabsTrigger>
            <TabsTrigger value="commissions" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Commissioni
            </TabsTrigger>
            <TabsTrigger value="assignments" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Assegnazioni
            </TabsTrigger>
          </TabsList>

          {/* Agents Tab */}
          <TabsContent value="agents">
            <div className="card-dashboard">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-bold">Lista Agenti</h2>
                <Button onClick={handleCreate} className="btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Agente
                </Button>
              </div>

              {agents.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nessun agente commerciale</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-sm">Agente</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Contatti</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Commissione</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Aziende</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Guadagnato</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Stato</th>
                        <th className="text-right py-3 px-4 font-medium text-sm">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agents.map((agent) => (
                        <tr key={agent.agent_id} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 px-4">
                            <p className="font-medium">{agent.name}</p>
                            {agent.notes && <p className="text-xs text-muted-foreground">{agent.notes}</p>}
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm">{agent.email}</p>
                            {agent.phone && <p className="text-xs text-muted-foreground">{agent.phone}</p>}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant="outline" className="font-mono">{agent.commission_rate}%</Badge>
                          </td>
                          <td className="py-3 px-4 text-center font-mono">{agent.total_companies}</td>
                          <td className="py-3 px-4 text-center">
                            <p className="font-mono text-green-600">€{agent.total_commissions_earned.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">Pagato: €{agent.total_commissions_paid.toFixed(2)}</p>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant={agent.is_active ? "default" : "secondary"}>
                              {agent.is_active ? "Attivo" : "Inattivo"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(agent)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(agent)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <div className="card-dashboard">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                  <h2 className="font-bold">Commissioni</h2>
                  <Select value={commissionFilter} onValueChange={setCommissionFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte</SelectItem>
                      <SelectItem value="pending">Da Pagare</SelectItem>
                      <SelectItem value="paid">Pagate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={handleExportCSV}>
                  <FileDown className="h-4 w-4 mr-2" />
                  Esporta CSV
                </Button>
              </div>

              {commissionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : commissions.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nessuna commissione</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-sm">Data</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Agente</th>
                        <th className="text-left py-3 px-4 font-medium text-sm">Azienda</th>
                        <th className="text-right py-3 px-4 font-medium text-sm">Transazione</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">%</th>
                        <th className="text-right py-3 px-4 font-medium text-sm">Commissione</th>
                        <th className="text-center py-3 px-4 font-medium text-sm">Stato</th>
                        <th className="text-right py-3 px-4 font-medium text-sm">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((comm) => (
                        <tr key={comm.commission_id} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 px-4 text-sm">
                            {format(new Date(comm.created_at), "dd/MM/yyyy", { locale: it })}
                          </td>
                          <td className="py-3 px-4 font-medium">{comm.agent_name}</td>
                          <td className="py-3 px-4">{comm.company_name}</td>
                          <td className="py-3 px-4 text-right font-mono">€{comm.transaction_amount.toFixed(2)}</td>
                          <td className="py-3 px-4 text-center font-mono">{comm.commission_rate}%</td>
                          <td className="py-3 px-4 text-right font-mono font-bold">€{comm.commission_amount.toFixed(2)}</td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant={comm.status === "paid" ? "default" : "outline"} className={comm.status === "paid" ? "bg-green-500" : "border-yellow-500 text-yellow-600"}>
                              {comm.status === "paid" ? "Pagata" : "Da Pagare"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {comm.status === "pending" && (
                              <Button variant="outline" size="sm" onClick={() => handlePayCommission(comm)}>
                                <Check className="h-4 w-4 mr-1" />
                                Paga
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments">
            <div className="card-dashboard">
              <h2 className="font-bold mb-4">Aziende e Agenti Assegnati</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-sm">Azienda</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Agente Assegnato</th>
                      <th className="text-right py-3 px-4 font-medium text-sm">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company) => {
                      const assignedAgent = agents.find(a => a.agent_id === company.sales_agent_id);
                      return (
                        <tr key={company.company_id} className="border-b border-border hover:bg-muted/50">
                          <td className="py-3 px-4">
                            <p className="font-medium">{company.name}</p>
                            <p className="text-xs text-muted-foreground">{company.email}</p>
                          </td>
                          <td className="py-3 px-4">
                            {assignedAgent ? (
                              <div className="flex items-center gap-2">
                                <Badge>{assignedAgent.name}</Badge>
                                <span className="text-xs text-muted-foreground">({assignedAgent.commission_rate}%)</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Select
                              value={company.sales_agent_id || "none"}
                              onValueChange={(v) => handleAssignAgent(company, v === "none" ? null : v)}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Seleziona agente" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Nessun agente</SelectItem>
                                {agents.filter(a => a.is_active).map((agent) => (
                                  <SelectItem key={agent.agent_id} value={agent.agent_id}>
                                    {agent.name} ({agent.commission_rate}%)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAgent ? "Modifica Agente" : "Nuovo Agente"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="input-label">Nome *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Mario Rossi"
              />
            </div>

            <div>
              <label className="input-label">Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="agente@email.com"
              />
            </div>

            <div>
              <label className="input-label">Telefono</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+39 333 1234567"
              />
            </div>

            <div>
              <label className="input-label">Commissione (%)</label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={formData.commission_rate}
                onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) || 0 })}
              />
            </div>

            <div>
              <label className="input-label">Note</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note interne..."
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="is_active" className="text-sm">Agente attivo</label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingAgent ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
